import { ArrowsClockwise, WarningCircle, X } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { formatUpdatedAt, statusTone } from '../lib/constants'

export function Button({
  children,
  variant = 'default',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost' }) {
  return <button className={`button button-${variant} ${className}`} {...props}>{children}</button>
}

export function StatusBadge({ status, tone }: { status: string; tone?: 'white' | 'blue' | 'green' | 'yellow' | 'red' | 'gray' }) {
  return <span className={`status-badge tone-${tone ?? statusTone(status)}`}>{status}</span>
}

export function CategoryBadge({ value }: { value: string }) {
  const className = value.toLowerCase().replaceAll(' ', '-').replaceAll('_', '-')
  return <span className={`category-badge category-${className}`}>{value}</span>
}

export function Drawer({
  open,
  title,
  subtitle,
  children,
  footer,
  onClose,
}: {
  open: boolean
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  onClose: () => void
}) {
  if (!open) return null
  return (
    <div className="overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header className="drawer-header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="關閉"><X size={20} /></button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-footer">{footer}</footer>}
      </aside>
    </div>
  )
}

export function PageHeading({
  eyebrow = 'WEEKLY READINESS',
  title,
  description,
  actions,
}: {
  eyebrow?: string
  title: string
  description: string
  actions?: ReactNode
}) {
  return (
    <header className="page-heading">
      <div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  )
}

export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`panel ${className}`}>{children}</section>
}

export function UpdatedMeta({ by, at }: { by: string; at: string }) {
  return <span className="updated-meta">{by || 'System'} · {formatUpdatedAt(at)}</span>
}

export function MetricCard({ label, value, detail, tone = 'navy' }: {
  label: string
  value: ReactNode
  detail?: string
  tone?: 'navy' | 'blue' | 'green' | 'amber'
}) {
  return (
    <div className={`metric-card metric-${tone}`}>
      <span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}
    </div>
  )
}

export function ConflictDialog({
  open,
  local,
  remote,
  labels,
  onUseLatest,
  onClose,
}: {
  open: boolean
  local: Record<string, unknown>
  remote: Record<string, unknown>
  labels: Record<string, string>
  onUseLatest: () => void
  onClose: () => void
}) {
  if (!open) return null
  const changed = Object.keys(labels).filter((key) => JSON.stringify(local[key]) !== JSON.stringify(remote[key]))
  return (
    <div className="overlay conflict-overlay" role="presentation">
      <section className="conflict-dialog" role="alertdialog" aria-modal="true" aria-labelledby="conflict-title">
        <div className="conflict-title"><WarningCircle size={28} weight="fill" /><div><h2 id="conflict-title">這筆資料已被其他人更新</h2><p>系統沒有覆蓋任何內容。請比較差異，再載入最新版本繼續編輯。</p></div></div>
        <div className="conflict-table" role="table">
          <div className="conflict-row conflict-head" role="row"><span>欄位</span><span>你的版本</span><span>伺服器最新版本</span></div>
          {changed.length > 0 ? changed.map((key) => (
            <div className="conflict-row" role="row" key={key}>
              <strong>{labels[key]}</strong><span>{displayValue(local[key])}</span><span className="remote-value">{displayValue(remote[key])}</span>
            </div>
          )) : <p className="no-differences">欄位內容相同，但版本號已改變。</p>}
        </div>
        <footer className="dialog-actions"><Button onClick={onClose}>保留我的內容</Button><Button variant="primary" onClick={onUseLatest}><ArrowsClockwise size={17} />載入最新內容</Button></footer>
      </section>
    </div>
  )
}

function displayValue(value: unknown) {
  if (Array.isArray(value)) return value.join('、') || '—'
  if (typeof value === 'boolean') return value ? '是' : '否'
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="empty-state"><strong>{title}</strong>{children && <p>{children}</p>}</div>
}

export function LoadingScreen() {
  return <div className="center-screen"><div className="loading-mark">ESS</div><p>正在載入資料…</p></div>
}
